import { useCallback } from "react";

import { ApolloError, useMutation } from "@apollo/client";

import {
  UPDATE_WORKSPACE,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import {
  Workspace,
  WorkspaceDashboard,
  WorkspaceDashboardInput,
  WorkspaceType,
  WorkspaceUpdate
} from "models/workspace";

interface UseUpdateWorkspaceOptions {
  // The title comes back so the caller can name the workspace it just saved, which it otherwise
  // has no handle on once the form has moved on.
  onSuccess?: (title: string) => void;
  onError?: (error: string) => void;
}

export function useUpdateWorkspace({
  onSuccess,
  onError
}: UseUpdateWorkspaceOptions = {}) {
  const [updateWorkspaceMutation, { loading: isLoading }] = useMutation(UPDATE_WORKSPACE);

  const mutate = useCallback(
    (
      workspace: Workspace, // expects the current workspace object
      workspaceId: string,
      title: string,
      type: WorkspaceType
    ) => {
      if (!workspace) return;

      const updatedWorkspace: WorkspaceUpdate = {
        workspaceId: workspaceId,
        title: title,
        type: type
      };

      if (workspace.type !== type && type !== "Personal") {
        // Remove any personal dashboards from the non-personal workspace.
        const updatedDashboards: WorkspaceDashboardInput[] = workspace.dashboards.reduce(
          (list: WorkspaceDashboardInput[], d: WorkspaceDashboard) => {
            if (d.type !== "Personal") {
              list.push({
                dashboardId: d.dashboardId,
                favourite: true
              });
            }
            return list;
          },
          []
        );
        updatedWorkspace.dashboards = updatedDashboards;
      }

      updateWorkspaceMutation({
        variables: {
          workspace: updatedWorkspace
        },
        context: {
          clientName: WorkspaceClientName
        },
        refetchQueries: [
          {
            query: WORKSPACES,
            context: { clientName: WorkspaceClientName }
          },
          {
            query: WORKSPACE,
            variables: { id: workspaceId },
            context: { clientName: WorkspaceClientName }
          }
        ],
        onCompleted: () => {
          // Clearing is onSuccess's job — signalling it through onError forces every
          // consumer's error state to treat "" as a sentinel for "no error".
          onSuccess?.(title);
        },
        onError: (error: ApolloError) => {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          onError?.(error.message);
        }
      });
    },
    [updateWorkspaceMutation, onSuccess, onError]
  );

  return { mutate, isLoading };
}
